import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const CATALOG_SECTION_ID = 'personalise-catalogs';

/** @type {Promise<{flag: Element, icon: Element}> | null} */
let catalogsPromise = null;

/**
 * Fetches the flag and icon catalogs (sections/personalise-catalogs.liquid) once per page.
 * They are kept out of the initial HTML because they run to ~350 hidden buttons/images.
 * @returns {Promise<{flag: Element, icon: Element}>}
 */
function loadCatalogs() {
  if (!catalogsPromise) {
    const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    url.searchParams.set('section_id', CATALOG_SECTION_ID);
    catalogsPromise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const flag = doc.querySelector('[data-catalog="flag"]');
        const icon = doc.querySelector('[data-catalog="icon"]');
        if (!flag || !icon) throw new Error('catalog markup missing');
        return { flag, icon };
      })
      .catch((error) => {
        catalogsPromise = null;
        throw error;
      });
  }
  return catalogsPromise;
}

/**
 * Copies the catalogs into every flag list and icon gallery in the dialog that
 * hasn't been filled yet. Safe to call repeatedly.
 * @param {HTMLElement} root - The [data-personalise-root] dialog-component element.
 */
function ensureCatalogs(root) {
  const targets = Array.from(
    root.querySelectorAll('[data-flag-list]:not([data-catalog-filled]), [data-icon-gallery]:not([data-catalog-filled])')
  );
  if (targets.length === 0) return;

  root.querySelectorAll('[data-catalog-error]').forEach((el) => el.remove());

  loadCatalogs()
    .then((catalogs) => {
      targets.forEach((target) => {
        if (target.hasAttribute('data-catalog-filled')) return;
        target.setAttribute('data-catalog-filled', '');
        const source = target.hasAttribute('data-flag-list') ? catalogs.flag : catalogs.icon;
        target.append(...document.importNode(source, true).childNodes);
        limitIconCategory(target);
      });
    })
    .catch((error) => {
      console.warn('[personalise-options] Failed to load catalogs:', error);
      targets.forEach((target) => {
        const message = document.createElement('p');
        message.className = 'personalise-flag-select__empty';
        message.setAttribute('data-catalog-error', '');
        message.append("Couldn't load these options. ");
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button-unstyled';
        retry.textContent = 'Try again';
        retry.addEventListener('click', () => ensureCatalogs(root));
        message.append(retry);
        target.append(message);
      });
    });
}

/**
 * For an icon gallery limited to one category (data-only-category, e.g. a bag's
 * "Graffiti Icon" option), drops the category tabs and shows only that
 * category's swatches.
 * @param {Element} gallery - A freshly filled [data-icon-gallery] element.
 */
function limitIconCategory(gallery) {
  const category = gallery.getAttribute('data-only-category');
  if (!category) return;
  gallery.querySelector('[data-icon-categories]')?.remove();
  gallery.querySelectorAll('[data-icon-swatch]').forEach((swatch) => {
    swatch.hidden = swatch.getAttribute('data-icon-category') !== category;
  });
}

/**
 * Wires up the swatch grid inside an image-choice option: clicking a swatch
 * selects it, stores its name in the hidden property input and echoes it below.
 * If the block requires a choice, blocks add-to-cart while it is on Yes with
 * nothing picked.
 * @param {Element} panel - The [data-image-choice-panel] element.
 */
function initImageChoicePanel(panel) {
  const valueInput = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-image-choice-value]'));
  const selectedText = /** @type {HTMLElement | null} */ (panel.querySelector('[data-image-choice-selected]'));
  if (!valueInput) return;

  panel.addEventListener('click', (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('[data-image-choice]') : null;
    if (!swatch || !panel.contains(swatch)) return;
    panel.querySelectorAll('[data-image-choice]').forEach((s) => s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'));
    valueInput.value = swatch.getAttribute('data-value') || '';
    if (selectedText) {
      selectedText.textContent = `Selected: ${valueInput.value}`;
      selectedText.hidden = false;
    }
  });

  if (valueInput.dataset.required === 'true') {
    // Listen on window in the capture phase: component.js dispatches on:submit from a
    // document-level capture listener, so anything registered on the form itself runs too late.
    window.addEventListener('submit', (event) => {
      if (event.target !== valueInput.form || valueInput.disabled || valueInput.value) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (selectedText) {
        selectedText.textContent = 'Please choose one, or switch this option to No.';
        selectedText.hidden = false;
      }
      // The product form's own submit handler ignores defaultPrevented, so stopping
      // propagation is what actually blocks the add. Reopen the dialog so the
      // customer can see what's missing.
      const root = /** @type {any} */ (panel.closest('[data-personalise-root]'));
      root?.showDialog?.();
      requestAnimationFrame(() => panel.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }, true);
  }
}

/**
 * Wires up the Yes/No personalisation option toggles inside a single Personalise
 * dialog, and follows up a successful "Add to cart" for the main product by adding
 * the linked add-on product for each selected paid option - so the extra charge is
 * actually collected at checkout, without touching the shared product-form component.
 * @param {HTMLElement} root - The [data-personalise-root] dialog-component element.
 */
function initPersonaliseRoot(root) {
  root.addEventListener('dialog:open', () => ensureCatalogs(root));
  const trigger = root.querySelector('.personalise__trigger');
  if (trigger) {
    ['pointerenter', 'focus', 'touchstart'].forEach((type) => {
      trigger.addEventListener(type, () => ensureCatalogs(root), { once: true, passive: true });
    });
  }

  root.querySelectorAll('[data-personalise-either-or]').forEach(initEitherOrGroup);
  root.querySelectorAll('[data-flag-select]').forEach(initFlagSelect);
  root.querySelectorAll('[data-icon-gallery-panel]').forEach(initIconGalleryPanel);
  root.querySelectorAll('[data-image-choice-panel]').forEach(initImageChoicePanel);

  const optionRows = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-personalise-option]')));
  if (optionRows.length === 0) return;

  const totalEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-personalise-total]'));
  const totalAmountEl = root.querySelector('[data-personalise-total-amount]');
  const currency = root.dataset.currency || 'USD';
  const moneyFormat = root.dataset.moneyFormat || '{{amount}}';

  /** @returns {HTMLElement[]} option rows currently toggled to "Yes" */
  function getSelectedRows() {
    return optionRows.filter(
      (row) => row.querySelector('[data-personalise-toggle="yes"]')?.getAttribute('aria-pressed') === 'true'
    );
  }

  function updateTotal() {
    if (!totalEl || !totalAmountEl) return;

    const totalCents = getSelectedRows().reduce((sum, row) => {
      const price = parseFloat(row.dataset.price || '0');
      return sum + Math.round(price * 100);
    }, 0);

    if (totalCents > 0) {
      totalAmountEl.textContent = formatMoney(totalCents, moneyFormat, currency);
      totalEl.hidden = false;
    } else {
      totalEl.hidden = true;
    }
  }

  optionRows.forEach((row) => {
    const yesBtn = row.querySelector('[data-personalise-toggle="yes"]');
    const noBtn = row.querySelector('[data-personalise-toggle="no"]');
    if (!yesBtn || !noBtn) return;

    const iconPanel = row.querySelector('[data-icon-panel]');
    const flagPanel = row.querySelector('[data-flag-option-panel]');
    const textPanel = row.querySelector('[data-text-option-panel]');
    const imageChoicePanel = row.querySelector('[data-image-choice-panel]');
    const plainValue = row.querySelector('[data-option-value]');

    const setSelected = (selected) => {
      yesBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      noBtn.setAttribute('aria-pressed', selected ? 'false' : 'true');
      if (iconPanel) {
        iconPanel.hidden = !selected;
        iconPanel.querySelectorAll('input').forEach((input) => {
          input.disabled = !selected || isInactiveIconInput(input, iconPanel);
        });
      }
      if (flagPanel) {
        flagPanel.hidden = !selected;
        flagPanel.querySelectorAll('input').forEach((input) => {
          input.disabled = !selected;
        });
      }
      if (textPanel) {
        textPanel.hidden = !selected;
        textPanel.querySelectorAll('input').forEach((input) => {
          input.disabled = !selected;
        });
      }
      if (imageChoicePanel) {
        imageChoicePanel.hidden = !selected;
        imageChoicePanel.querySelectorAll('input').forEach((input) => {
          input.disabled = !selected;
        });
      }
      // Plain Yes/No options have no field of their own, so submit a "Yes"
      // property only while selected - otherwise a free option leaves no trace on the order.
      if (plainValue) plainValue.disabled = !selected;
      updateTotal();
    };

    yesBtn.addEventListener('click', () => setSelected(true));
    noBtn.addEventListener('click', () => setSelected(false));
  });

  root.querySelectorAll('[data-personalise-icon-option]').forEach(initIconOptionPanel);

  updateTotal();

  const productId = root.dataset.productId;
  const productTitle = root.dataset.productTitle || '';
  if (!productId) return;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || detail.source !== 'product-form-component') return;
        if (String(detail.productId) !== String(productId)) return;
        if (detail.didError) return;

        const selectedAddons = getSelectedRows().filter((row) => row.dataset.addonVariantId);
        if (selectedAddons.length === 0) return;

        const quantity = detail.itemCount || 1;
        const items = selectedAddons.map((row) => ({
          id: Number(row.dataset.addonVariantId),
          quantity,
          properties: {
            'For product': productTitle,
            Personalisation: row.dataset.label || '',
          },
        }));

        addAddonsToCart(items);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[personalise-options] Event promise rejected:', error);
      });
  });
}

/**
 * Determines whether an input inside an icon-option's sub-panel belongs to the
 * currently inactive mode (gallery vs upload), so it can be disabled and excluded
 * from form submission even while the panel itself is visible.
 * @param {HTMLInputElement} input
 * @param {Element} iconPanel
 */
function isInactiveIconInput(input, iconPanel) {
  const galleryActive = iconPanel.querySelector('[data-icon-mode="gallery"]')?.getAttribute('aria-pressed') === 'true';
  if (input.hasAttribute('data-icon-value')) return !galleryActive;
  if (input.hasAttribute('data-personalise-file-input')) return !!galleryActive;
  return false;
}

/**
 * Wires up the "choose from gallery" / "upload your own" mode switch and the
 * gallery swatch selection inside a single icon option's sub-panel.
 * @param {Element} row - The [data-personalise-icon-option] element.
 */
function initIconOptionPanel(row) {
  const panel = row.querySelector('[data-icon-panel]');
  if (!panel) return;
  initIconGalleryPanel(panel);
}

/**
 * Wires up the "choose from gallery" / "upload your own" mode switch and the
 * gallery swatch selection inside any panel containing [data-icon-gallery]
 * markup - shared by icon options and the either/or block's icon choice.
 * @param {Element} panel
 */
function initIconGalleryPanel(panel) {
  const galleryBtn = panel.querySelector('[data-icon-mode="gallery"]');
  const uploadBtn = panel.querySelector('[data-icon-mode="upload"]');
  const gallery = panel.querySelector('[data-icon-gallery]');
  const uploadWrapper = panel.querySelector('[data-icon-upload]');
  const valueInput = panel.querySelector('[data-icon-value]');
  const fileInput = panel.querySelector('[data-personalise-file-input]');
  if (!galleryBtn || !uploadBtn || !gallery || !uploadWrapper) return;

  function setMode(mode) {
    const isGallery = mode === 'gallery';
    galleryBtn.setAttribute('aria-pressed', isGallery ? 'true' : 'false');
    uploadBtn.setAttribute('aria-pressed', isGallery ? 'false' : 'true');
    // Both stay laid out (see .personalise-icon-option__stage) so the inactive
    // one still reserves its space - toggling `hidden` here would let the
    // dialog resize every time the mode is switched.
    gallery.toggleAttribute('data-icon-inactive', !isGallery);
    uploadWrapper.toggleAttribute('data-icon-inactive', isGallery);
    const panelActive = !panel.hidden;
    if (valueInput) valueInput.disabled = !panelActive || !isGallery;
    if (fileInput) fileInput.disabled = !panelActive || isGallery;
  }

  galleryBtn.addEventListener('click', () => setMode('gallery'));
  uploadBtn.addEventListener('click', () => setMode('upload'));

  // Sync disabled state with the default-rendered mode (gallery) and the panel's
  // current visibility, since the server-rendered markup always marks both the
  // gallery value input and the upload input disabled until this runs.
  setMode('gallery');

  // Delegated: the tabs and swatches are loaded after init (see ensureCatalogs).
  panel.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const tab = target.closest('[data-icon-categories] [data-icon-category]');
    if (tab && panel.contains(tab)) {
      const category = tab.getAttribute('data-icon-category');
      panel.querySelectorAll('[data-icon-categories] [data-icon-category]').forEach((t) => {
        t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
      });
      panel.querySelectorAll('[data-icon-swatch]').forEach((swatch) => {
        swatch.hidden = swatch.getAttribute('data-icon-category') !== category;
      });
      return;
    }

    const swatch = target.closest('[data-icon-swatch]');
    if (swatch && panel.contains(swatch)) {
      panel.querySelectorAll('[data-icon-swatch]').forEach((s) => s.setAttribute('aria-pressed', 'false'));
      swatch.setAttribute('aria-pressed', 'true');
      if (valueInput instanceof HTMLInputElement) valueInput.value = swatch.getAttribute('aria-label') || '';
    }
  });
}

/**
 * Wires up a mutually-exclusive pair of personalisation choices: selecting one
 * reveals its field and disables the other so only the active choice submits.
 * @param {Element} container - The [data-personalise-either-or] element.
 */
function initEitherOrGroup(container) {
  const buttons = Array.from(container.querySelectorAll('[data-either-or-choice]'));
  const panels = {
    a: container.querySelector('[data-either-or-panel="a"]'),
    b: container.querySelector('[data-either-or-panel="b"]'),
  };

  function setChoice(choice) {
    buttons.forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-either-or-choice') === choice ? 'true' : 'false');
    });
    Object.entries(panels).forEach(([key, panel]) => {
      if (!panel) return;
      const active = key === choice;
      panel.hidden = !active;
      const hasIconGallery = !!panel.querySelector('[data-icon-gallery]');
      panel.querySelectorAll('input').forEach((input) => {
        input.disabled = hasIconGallery ? !active || isInactiveIconInput(input, panel) : !active;
      });
    });
  }

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => setChoice(btn.getAttribute('data-either-or-choice')));
  });

  initLetterPreview(panels.a);
  if (panels.b?.querySelector('[data-icon-gallery]')) initIconGalleryPanel(panels.b);
}

/**
 * If an either/or panel's text field has an attached alphabet preview (see
 * snippets/_personalise-alphabet-preview.liquid), shows the matching
 * graffiti-style letter graphic as the customer types.
 * @param {Element | null | undefined} panel
 */
function initLetterPreview(panel) {
  if (!panel) return;
  const mapScript = panel.querySelector('[data-letter-map]');
  const previewImg = panel.querySelector('[data-letter-preview-img]');
  const textInput = panel.querySelector('input[type="text"]');
  if (!mapScript || !previewImg || !textInput) return;

  let letterMap;
  try {
    letterMap = JSON.parse(mapScript.textContent);
  } catch {
    return;
  }

  textInput.addEventListener('input', () => {
    const letter = textInput.value.trim().charAt(0).toUpperCase();
    const url = letterMap[letter];
    if (url) {
      previewImg.src = url;
      previewImg.alt = `Preview of letter ${letter}`;
      previewImg.hidden = false;
    } else {
      previewImg.hidden = true;
    }
  });
}

/**
 * Wires up the custom searchable flag/country dropdown. A native <select> can't
 * render an image inside its <option>s, so this is a button + popup panel of
 * real buttons (each with a flag image) instead, with a text filter since the
 * list runs to 200+ countries.
 * @param {Element} container - The [data-flag-select] element.
 */
function initFlagSelect(container) {
  const trigger = /** @type {HTMLElement | null} */ (container.querySelector('[data-flag-trigger]'));
  const panel = /** @type {HTMLElement | null} */ (container.querySelector('[data-flag-panel]'));
  const search = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-flag-search]'));
  const list = container.querySelector('[data-flag-list]');
  const valueInput = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-flag-value]'));
  const triggerContent = container.querySelector('[data-flag-trigger-content]');
  const emptyState = /** @type {HTMLElement | null} */ (container.querySelector('[data-flag-empty]'));
  if (!trigger || !panel || !search || !list || !valueInput || !triggerContent) return;

  const getOptions = () => /** @type {HTMLElement[]} */ (Array.from(list.querySelectorAll('[data-flag-option]')));

  function openPanel() {
    if (!panel || !search) return;
    panel.hidden = false;
    trigger?.setAttribute('aria-expanded', 'true');
    search.value = '';
    filterOptions('');
    search.focus({ preventScroll: true });

    // Start the list at the current choice rather than the top, and bring the whole
    // panel into view inside the (scrollable) dialog.
    const selected = getOptions().find((option) => option.getAttribute('aria-selected') === 'true');
    if (selected && list) {
      list.scrollTop +=
        selected.getBoundingClientRect().top - list.getBoundingClientRect().top - list.clientHeight / 2;
    }
    const dialog = panel.closest('dialog');
    if (dialog) {
      const dialogRect = dialog.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      // Scroll just enough to show the whole panel, but never past the trigger itself.
      const overflow = Math.min(
        panel.getBoundingClientRect().bottom - dialogRect.bottom + 24,
        triggerRect.top - dialogRect.top - 16
      );
      if (overflow > 0) dialog.scrollTop += overflow;
    }
  }

  /**
   * Moves focus by `step` through the visible options, wrapping back to the search box
   * when stepping above the first one.
   * @param {number} step
   */
  function moveFocus(step) {
    const visible = getOptions().filter((option) => !option.hidden);
    if (!visible.length) return;
    const index = visible.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    const next = index + step;
    if (next < 0) search?.focus();
    else visible[Math.min(next, visible.length - 1)].focus();
  }

  function closePanel() {
    if (!panel) return;
    panel.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
  }

  function filterOptions(query) {
    const normalized = query.trim().toLowerCase();
    let visibleCount = 0;
    getOptions().forEach((option) => {
      const matches = !normalized || (option.dataset.search || '').includes(normalized);
      option.hidden = !matches;
      if (matches) visibleCount += 1;
    });
    if (emptyState) emptyState.hidden = visibleCount > 0;
  }

  function selectOption(option) {
    if (!valueInput || !triggerContent) return;
    const value = option.dataset.value || '';
    const flagImg = option.querySelector('img');
    const name = option.querySelector('[data-flag-option-name]')?.textContent || '';

    triggerContent.innerHTML = '';
    if (flagImg instanceof HTMLImageElement) {
      const img = document.createElement('img');
      img.src = flagImg.src;
      img.alt = '';
      img.className = 'personalise-flag-select__trigger-flag';
      triggerContent.appendChild(img);
    }
    const label = document.createElement('span');
    label.textContent = name || 'None';
    triggerContent.appendChild(label);

    valueInput.value = value;
    getOptions().forEach((o) => o.setAttribute('aria-selected', o === option ? 'true' : 'false'));
    closePanel();
    trigger?.focus();
  }

  trigger.addEventListener('click', () => {
    if (panel.hidden) openPanel();
    else closePanel();
  });

  search.addEventListener('input', () => filterOptions(search.value));

  search.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closePanel();
      trigger?.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const firstVisible = getOptions().find((option) => !option.hidden);
      if (firstVisible) selectOption(firstVisible);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveFocus(1);
    }
  });

  list.addEventListener('keydown', (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveFocus(event.key === 'ArrowDown' ? 1 : -1);
    }
  });

  list.addEventListener('click', (event) => {
    const option = event.target instanceof Element ? event.target.closest('[data-flag-option]') : null;
    if (option instanceof HTMLElement && list.contains(option)) selectOption(option);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node) || !container.contains(event.target)) closePanel();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      closePanel();
      trigger?.focus();
    }
  });

  // A hidden input can't use the native `required` attribute the way the old
  // <select> could, so enforce it manually on the form it's associated with.
  if (valueInput.dataset.required === 'true') {
    valueInput.form?.addEventListener('submit', (event) => {
      if (!valueInput.value) {
        event.preventDefault();
        openPanel();
      }
    });
  }
}

/**
 * Adds the selected personalisation add-on products to the cart as their own line
 * items, and dispatches the standard cart-update event so the cart icon/drawer
 * refresh the same way they would for a regular add-to-cart.
 * @param {Array<{id: number, quantity: number, properties: Record<string, string>}>} items
 */
function addAddonsToCart(items) {
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    })
  );

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ items }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        throw new Error(response.message || 'Failed to add personalisation add-ons');
      }

      return fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());
    })
    .then((cart) => {
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'personalise-options', didError: false },
      });
    })
    .catch((error) => {
      deferredEventPromise.reject(error);
      console.error('[personalise-options] Failed to add personalisation add-ons:', error);
    });
}

function init() {
  document.querySelectorAll('[data-personalise-root]').forEach((root) => {
    if (root instanceof HTMLElement) initPersonaliseRoot(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
